import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { DxDataGridComponent } from "devextreme-angular";
import { custom } from "devextreme/ui/dialog";
import { Equipment } from "src/app/_api/models/equipment.model";
import { TimeService } from "src/app/modules/time/_api/services/time.service";
import { AmsNotifierService } from "src/app/shared/services/ams-notifier.service";
import { LanguageService } from "src/app/shared/services/language.service";
import { TimeConverter } from "src/app/shared/services/time-converter.service";

@Component({
  selector: "app-project-portal-approval-employee-equipments",
  templateUrl: "./equipments.component.html",
  styleUrls: ["./equipments.component.css"],
})
export class ProjectPortalApprovalEmployeeEquipmentsComponent {
  @Input() transportEquipments: Equipment[];
  @Input() equipments: any[];
  @Input() selectedTimeRecord: any;
  @Input() allowEdit: boolean;
  @Input() transport: any;

  @Output() isBusy = new EventEmitter<boolean>();
  @Output() refresh = new EventEmitter<boolean>(); //false if not clearing datagrid selections
  @Output() editingStart = new EventEmitter<any>();
  @Output() loading = new EventEmitter<boolean>(); //emit event to parent(project-portal.component) to show load indicator

  @ViewChild("datagriddx", { static: false })
  equipmentsGrid: DxDataGridComponent;

  constructor(
    public languageService: LanguageService,
    private timeService: TimeService,
    private amsNotifierService: AmsNotifierService,
    private timeConverter: TimeConverter) {}

  private async loadTransportEquipments(): Promise<any> {
    if (!this.selectedTimeRecord.IsTransport) {
      this.transportEquipments = (await this.timeService.getTransportEquipmentByCarrierEmployeeDate(this.selectedTimeRecord.CarrierId, 
        this.selectedTimeRecord.EmployeeId, this.selectedTimeRecord.Date).toPromise())?.Data; 
    }
    else this.transportEquipments = (await this.timeService.getTransportEquipmentByCarrier(this.selectedTimeRecord.CarrierId).toPromise())?.Data;
  }

  onToolbarPreparing(e: any) {
    if (this.allowEdit) {
      e.toolbarOptions.items.unshift({
          location: "after",
          widget: "dxButton",
          options: {
            icon: "trash",
            title: this.languageService.LNG("Label.Delete"),
            onClick: this.onDeleteSelected.bind(this),
          },
        }
      );
    }
  }

  onDeleteSelected() {
    let myDialog = custom({
      title: this.languageService.LNG("Label.Delete"),
      messageHtml: this.languageService.LNG(
        "Label.Do.You.Want.To.Delete.The.Selected.Rows"
      ),
      buttons: [
        {
          text: this.languageService.LNG("Label.Yes"),
          onClick: (e) => {
            return true;
          },
        },
        {
          text: this.languageService.LNG("Label.No"),
          onClick: (e) => {
            return false;
          },
        },
      ],
    });

    myDialog.show().then((confirmed: boolean) => {
      if (!confirmed) return;

      let equipmentIds: number[] = [];
      let selectedRowsData = this.equipmentsGrid.instance.getSelectedRowsData();

      if (selectedRowsData.length === 0) {
        this.amsNotifierService.error(this.languageService.LNG("Label.Please.Select.Rows.To.Delete"));
        return;        
      }

      for (let i = 0; i < selectedRowsData.length; i++) {
        equipmentIds.push(selectedRowsData[i].EquipmentId);
      }

      this.onLoading(true);

      this.timeService.deleteTransportEquipmentByCarrierAndEquipment(this.selectedTimeRecord.CarrierId, equipmentIds)
        .subscribe((result: any) => {
          this.onLoading(false);
          this.loadTransportEquipments();

          if (!result.IsSuccess) {
            this.amsNotifierService.error(result.ErrorMessage);
            return;
          }

          this.amsNotifierService.success(this.languageService.LNG("Label.DeletedOK"));

          if (result.Message != null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
          }

          this.refresh.emit(false);
          this.equipmentsGrid.selectedRowKeys = [];
        },
        (error) => {
          this.onLoading(false);
        }
      );
    });
  }

  onEditorPreparing(e: any) {
    if (e.parentType == "dataRow") {
      e.editorOptions.onFocusIn = function (args) {
        const input = args.element.querySelector(".dx-texteditor-input");
        if (input !== null && input !== undefined) {
          input.select();
        }
      };
    }
  }

  onEditingStart(e: any) {
    this.editingStart.emit(e);
  }

  async onInitNewRow(e: any) {
    e.data.CarrierId = this.selectedTimeRecord.CarrierId;
    e.data.LoadedEquipment = false;
    e.data.IsMainEquipment = false;
  }

  onRowInserting(e: any) {
    const isCanceled = new Promise((resolve) => {
      const seconds = this.timeConverter.getTimeInputAsHoursAndMinutesConvertedToSeconds(e.data.TotSecondsToHH, e.data.TotSecondsTomm);  
      const equipment = {
        CarrierId: this.selectedTimeRecord.CarrierId,
        EquipmentId: e.data.EquipmentId,
        IsMainEquipment: e.data.IsMainEquipment,
        LoadedEquipment: e.data.LoadedEquipment,
        Seconds: seconds,
      };

      this.onLoading(true);
      this.timeService.insertEquipment(equipment)
        .subscribe((result: any) => {  
          if (result.IsSuccess) {
            this.onLoading(false);
            resolve(false);
            this.amsNotifierService.success(this.languageService.LNG("Label.SavedOK"));
            if (result.Message != null && result.Message.length > 0) {
              this.amsNotifierService.error(result.Message);
            }
            e.component.cancelEditData();
            e.component.refresh(true);
            this.loadTransportEquipments();
          }
          else {
            this.onLoading(false);
            resolve(true);
            this.amsNotifierService.error(result.ErrorMessage);
          }
        }, error => {
          this.onLoading(false);
          resolve(true);
        });
    });

    e.cancel = isCanceled;
  }
  
  onRowUpdating(e: any) {
    const isCanceled = new Promise((resolve) => {
      for (let property in e.oldData) {
        if (!e.newData.hasOwnProperty(property)) {
          e.newData[property] = e.oldData[property];
        }
      }

      const seconds = this.timeConverter.getTimeInputAsHoursAndMinutesConvertedToSeconds(e.newData.TotSecondsToHH, e.newData.TotSecondsTomm);
      const equipment = {
        CarrierId: this.selectedTimeRecord.CarrierId,
        EquipmentId: e.newData.EquipmentId,
        IsMainEquipment: e.newData.IsMainEquipment,
        LoadedEquipment: e.newData.LoadedEquipment,
        Seconds: seconds,
      };
      let keyCarrierId = this.selectedTimeRecord.CarrierId;
      let keyEquipmentId = e.key.EquipmentId;

      this.loading.emit(true);  
      this.timeService.updateEquipment(keyCarrierId, keyEquipmentId, equipment.EquipmentId, equipment.IsMainEquipment, equipment.LoadedEquipment, equipment.Seconds)
        .subscribe((result: any) => {
          this.onLoading(false);
          if (result.IsSuccess) {            
            resolve(false);
            this.amsNotifierService.success(this.languageService.LNG("Label.SavedOK"));
            if (result.Message != null && result.Message.length > 0) {
              this.amsNotifierService.error(result.Message);
            }
            e.component.cancelEditData();
            e.component.refresh(true);
            this.loadTransportEquipments();
          }
          else {
            this.onLoading(false);
            resolve(true);
            this.amsNotifierService.error(result.ErrorMessage);
          }
        }, error => {
          this.onLoading(false);
          resolve(true);
        });
    });

    e.cancel = isCanceled;
  }

  setAllowEdit(allow: boolean) {
    this.allowEdit = allow;
  }

  onLoading(loading: boolean) {
    this.loading.emit(loading);
  }
}
